<template>
  <SectionError
    v-if="hasError"
    class="home-hero--error"
    title="Não foi possível carregar os dados neste momento"
    description="Aguarde alguns instantes e tente novamente."
    @on-retry="fetchHeroButtons"
  />
  <section v-else>
    <div class="home-hero">
      <HomeHeroActions
        :is-overdue="props.isOverdue"
        :is-loading="isLoading"
        :hero-buttons="heroButtons"
        :disable-actions="hasError"
        data-testid="home-hero-actions"
      />
    </div>
    <FadeTransition>
      <div id="home-hero-summary">
        <HomeHeroSummary
          :is-loading="isLoading"
          :balance="balance"
          :has-error="hasError"
          @click="redirectToTransactionsPage"
        />
      </div>
    </FadeTransition>
  </section>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router/composables';
import { FadeTransition } from '@/modules/common/components/FadeTransition';
import HomeHeroActions from './components/HomeHeroActions.vue';
import HomeHeroSummary from './components/HomeHeroSummary.vue';
import { trackBalanceAccountErrorLoad } from './track-events';
import { trackTransactionsPageNavigation } from '@/modules/transaction/helpers/transactions-events';
import { WarrenModules } from '@/types/navigation';
import * as trackEvents from './track-events';
import {
  getHeroBalanceAndButtons,
  HomeBalanceHero
} from '../../services/balances';
import { SectionError } from '@/modules/common/components/SectionError';

const props = defineProps<{
  isOverdue: boolean;
}>();

const router = useRouter();

const isLoading = ref(true);
const hasError = ref(false);
const heroButtons = ref<HomeBalanceHero | null>(null);

onMounted(() => {
  fetchHeroButtons();
});

async function fetchHeroButtons() {
  isLoading.value = true;
  hasError.value = false;

  try {
    heroButtons.value = await getHeroBalanceAndButtons();
  } catch {
    hasError.value = true;
    trackBalanceAccountErrorLoad();
  } finally {
    isLoading.value = false;
  }
}

function getValueWithDefault(value?: string | null, defaultValue = 'R$ 0,00') {
  return value || defaultValue;
}

const balance = computed(() =>
  getValueWithDefault(heroButtons.value?.accountBalance, '')
);

function redirectToTransactionsPage() {
  router.push({ name: 'transactions' });

  trackTransactionsPageNavigation(WarrenModules.Home);
  trackEvents.trackViewMoreTransactionsEvent();
}
</script>

<style lang="less" scoped>
@max-width: 1008px;

.home-hero {
  max-width: @max-width;
  margin: @size-spacing-x1000 auto;

  &__balance-container {
    display: flex;
    align-items: center;

    .transit-loader {
      margin-left: @size-spacing-x900;
      &__title {
        width: 107px;
        height: 20px;
        border-radius: @size-radius-x100;
        margin-bottom: @size-spacing-x150;
      }
      &__total {
        width: 107px;
        height: 30px;
        border-radius: @size-radius-x100;
      }
    }

    .heading-display {
      margin-left: @size-spacing-x900;
    }

    .balance-transit {
      display: flex;
      align-items: center;
    }
  }

  .to-lg({
    flex-flow: column;
    align-items: flex-start;

    &__balance-container {
      width: 100%;
      margin-bottom: @size-spacing-x700;
    }
  });

  &--error {
    min-width: @max-width;
    margin: 0 auto @size-spacing-x600;
    margin: 64px auto;
  }
}
</style>
